import { DateTime } from 'luxon';
import { capitalize, markRaw } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import allPollens from '@/common/components/controls/targeting/pollen/PollenTargetingEnumerations';
import allUVs from '@/common/components/controls/targeting/uv/UVTargetingEnumerations';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import CreativeType from '@/common/enums/CreativeType.enum';
import RunState from '@/common/enums/run-state.enum';
import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
import {
  IconColoredDaypart,
  IconColoredEvent,
  IconColoredLocation as IconColoredGeography,
  IconColoredPollen,
  IconColoredWeather,
} from '@/common/icons/colored';
import { IconDaypart, IconEvent, IconLocation as IconGeography, IconWeather } from '@/common/icons/monochrome';
import {
  CreativeFile,
  CreativeMediaApproval,
  CreativeProperty,
  IABCategories,
  IABCategory,
  LineItemCreative,
  Market,
  MediaType,
  TargetingCriterion,
} from '@/common/models';
import Advertiser, { IAdvertiserAPI } from '@/common/models/advertisers/Advertiser';
import { getEntityProperties } from '@/common/models/creative/Creative.entityProperties';
import { ICreativeFileAPI } from '@/common/models/creative/CreativeFile';
import CreativeFileApprovalStatus from '@/common/models/creative/CreativeFileApprovalStatus.enum';
import { ICreativeMediaApprovalAPI } from '@/common/models/creative/CreativeMediaApproval';
import CreativePrefetchSize, { ICreativePrefetchSizeAPI } from '@/common/models/creative/CreativePrefetchSize';
import { ICreativePropertyAPI } from '@/common/models/creative/CreativeProperty';
import Status from '@/common/models/creative/CreativeStatus.enum';
import CreativeTargetingCriterion, {
  ICreativeTargetingCriterionAPI,
} from '@/common/models/creative/CreativeTargetingCriterion';
import CreativeTrackingUrl from '@/common/models/creative/CreativeTrackingUrl';
import CreativeTypeOptions from '@/common/models/creative/CreativeTypeOptions';
import PrefetchSizeApprovalStatus from '@/common/models/creative/PrefetchSizeApprovalStatus.enum';
import { daypartToRanges, preciseDaypartToRanges } from '@/common/models/Dayparting';
import Exception from '@/common/models/Exception';
import InventoryTargeting, { IInventoryTargetingAPI } from '@/common/models/InventoryTargeting';
import { ILineItemCreativeAPI } from '@/common/models/lineitem/LineItemCreative';
import MediaOwnerApprovalStatus from '@/common/models/media-owner/MediaOwnerApprovalStatus.enum';
import Organization, { IOrganizationAPI } from '@/common/models/memberships/Organization';
import { FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import {
  DASH,
  fromServerDate,
  fromServerDateTime,
  FullDaypart,
  FullPreciseDaypart,
  getValueOrDash,
  groupBy,
  isValidTokenizedURL,
  toServerDate,
  toServerDateTime,
} from '@/globals';
import { useLanguages } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import rootState from '@/store/state';
import { getDiff } from '@/utils/Diff';
import { mapExchAppState, mapOrgAppState } from '@/utils/StateMapping';
import Validatable, { IEntityChange } from '@/validator/Validatable';
import i18n from '@/vueI18n';

export const ValidImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
export const ValidAudioTypes = ['audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/mpeg', 'audio/flac'];
export const ValidZipTypes = ['application/zip', 'application/x-zip-compressed'];

const type = 'Creative';

export interface ITrackingUrlAPI {
  url: string;
  multiple_calls: boolean;
}

export interface IPackageCreativeAPI {
  packagecreative_id: number;
  package_id: number;
  deal_id: number;
  package_state: string;
}

export interface ICreativeAPI {
  creative_id: number;
  name: string;
  uuid: string;
  advertiser_id: number;
  advertiser: IAdvertiserAPI;
  state: string;
  created_at: string;
  submitted_at: string;
  approval_at: string;
  approval_comment: string;
  retired_at: string;
  iab_categories: string[];
  creative_properties: ICreativePropertyAPI[];
  creative_files: ICreativeFileAPI[];
  language_ids: number[];
  lineitem_creatives: ILineItemCreativeAPI[];
  organization_id: number;
  organization: IOrganizationAPI;
  run_state: string;
  prefetch_type: string;
  prefetch_source: string;
  prefetch_id: string;
  prefetch_cache_type: string;
  prefetch_all_sizes_approval: string;
  prefetch_sizes: ICreativePrefetchSizeAPI[];
  is_fallback: boolean;
  is_mobile: boolean;
  tracking: ITrackingUrlAPI[] | null;
  network_ids: number[];
  network_exclusion_ids: number[];
  location_ids: number[];
  location_exclusion_ids: number[];
  site_ids: number[];
  site_exclusion_ids: number[];
  unit_ids: number[];
  unit_exclusion_ids: number[];
  geofencegroup_ids: number[];
  market_ids: number[];
  market_exclusion_ids: number[];
  mediatype_ids: number[];
  mediatype_exclusion_ids: number[];
  daypart: string;
  precise_daypart: string;
  package_creatives: IPackageCreativeAPI[];
  creative_targetingcriteria: ICreativeTargetingCriterionAPI[];
  override_default_duration: boolean;
  unit_tags: string[];
  unit_tag_exclusions: string[];
  creative_formats: string[];
  creative_format_exclusions: string[];
  creative_orientations: CreativeOrientationType[];
  creative_orientation_exclusions: CreativeOrientationType[];
  inventory_targeting: IInventoryTargetingAPI;
  mediaowner_approvals: ICreativeMediaApprovalAPI[];
  last_played_time_utc: number;
  flight_from: string;
  flight_to: string;
  creative_type?: CreativeType;
  co2e_emissions_score_threshold?: number;
}

export default class Creative extends Validatable {
  static isGettingMarkets = false;
  static marketsCache: Market[] = [];
  static isGettingMediaTypes = false;
  static mediaTypesCache: MediaType[] = [];

  apiObject: ICreativeAPI;
  cachedOriginalObject: Creative;

  name: string;
  uuid: string;
  advertiserID: number;
  advertiser: Advertiser;
  organizationID: number;
  organizationName: string;
  state: string;
  createdOn: DateTime | null;
  submittedOn: DateTime | null;
  approvedOn: DateTime | null;
  approvalComment: string | null;
  retiredOn: DateTime | null;
  lineItemIDs: number[] = [];
  creativeFiles: CreativeFile[] = [];
  iabCategories: IABCategory[] = [];
  mediaOwnerApprovals: CreativeMediaApproval[] = [];
  creativeProperties: CreativeProperty[] = [];
  languageIDs: number[] = [];
  lastPlayedTime: DateTime | null;
  runState = RunState.PLAYING;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  prefetchType?: string = null;
  prefetchSource: string | null;
  prefetchID: string | null;
  prefetchCacheType = 'M';
  prefetchAllSizesApproval?: string;
  prefetchSizes: CreativePrefetchSize[] = [];
  isFallback = false;
  isMobile = false;
  trackingUrls: CreativeTrackingUrl[] = [];
  mobileLineItemIDs: number[] = [];
  geofenceGroupIDs: number[] = [];
  geofenceGroupExclusionIDs: number[] = [];
  locationIDs: number[] = [];
  locationExclusionIDs: number[] = [];
  marketIDs: number[] = [];
  marketExclusionIDs: number[] = [];
  mediaTypeIDs: number[] = [];
  mediaTypeExclusionIDs: number[] = [];
  networkIDs: number[] = [];
  networkExclusionIDs: number[] = [];
  siteIDs: number[] = [];
  siteExclusionIDs: number[] = [];
  unitIDs: number[] = [];
  unitExclusionIDs: number[] = [];
  unitPackIDs: number[] = [];
  unitPackExclusionIDs: number[] = [];
  mediaOwnerIDs: number[] = [];
  daypart: string = FullDaypart;
  preciseDaypart: string = FullPreciseDaypart;
  packageCreatives: IPackageCreativeAPI[] = [];
  creativeTargetingCriteria: CreativeTargetingCriterion[] = [];
  overrideDefaultDuration = false;
  unitTags: string[] = [];
  unitTagExclusions: string[] = [];
  estimatedStart?: DateTime;
  estimatedEnd?: DateTime;
  creativeFormats: string[] = [];
  creativeFormatExclusions: string[] = [];
  creativeOrientations: CreativeOrientationType[] = [];
  creativeOrientationExclusions: CreativeOrientationType[] = [];
  lineItems: LineItemCreative[] = [];
  type: string;
  timestamp: DateTime;
  matchingCreativeSizes: { dimensions: number[] }[] = [];
  missingCreativeSizes: string[];
  inventoryTargetingID?: number;
  creativeType?: CreativeType;
  co2eEmissionsScoreThreshold?: number;

  constructor(
    id = -1,
    name = '',
    uuid = null,
    advertiserID = null,
    advertiser = null,
    organizationID = mapOrgAppState()?.organization?.id,
    organizationName = null,
    state = null,
    createdOn?: DateTime,
    submittedOn?: DateTime,
    approvedOn?: DateTime,
    approvalComment = null,
    retiredOn?: DateTime,
    lineItemIDs = [],
    creativeFiles = [],
    iabCategories = [],
    mediaOwnerApprovals = [],
    creativeProperties = [],
    languageIDs = [],
    lastPlayedTime = null,
    runState = RunState.PLAYING,
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    prefetchType = null,
    prefetchSource = null,
    prefetchID = null,
    prefetchCacheType = 'M',
    prefetchAllSizesApproval = null,
    prefetchSizes = [],
    isFallback = false,
    isMobile = false,
    trackingUrls = [],
    mobileLineItemIDs: number[] = [],
    geofenceGroupIDs: number[] = [],
    geofenceGroupExclusionIDs: number[] = [],
    locationIDs: number[] = [],
    locationExclusionIDs: number[] = [],
    marketIDs: number[] = [],
    marketExclusionIDs: number[] = [],
    mediaTypeIDs: number[] = [],
    mediaTypeExclusionIDs: number[] = [],
    networkIDs: number[] = [],
    networkExclusionIDs: number[] = [],
    siteIDs: number[] = [],
    siteExclusionIDs: number[] = [],
    unitIDs: number[] = [],
    unitExclusionIDs: number[] = [],
    unitPackIDs: number[] = [],
    unitPackExclusionIDs: number[] = [],
    mediaOwnerIDs: number[] = [],
    daypart: string = FullDaypart,
    preciseDaypart: string = FullPreciseDaypart,
    packageCreatives = [],
    creativeTargetingCriteria: CreativeTargetingCriterion[] = [],
    overrideDefaultDuration = false,
    unitTags = [],
    unitTagExclusions = [],
    estimatedStart?: DateTime,
    estimatedEnd?: DateTime,
    creativeFormats = [],
    creativeFormatExclusions = [],
    inventoryTargetingID = -1,
    creativeOrientations = [],
    creativeOrientationExclusions = [],
    creativeType = null,
    co2eEmissionsScoreThreshold = null,
  ) {
    super();
    this.type = type;
    this.id = id;
    this.name = name;
    this.uuid = uuid;
    this.advertiserID = advertiserID;
    this.advertiser = advertiser;
    this.organizationID = organizationID;
    this.organizationName = organizationName;
    this.state = state;
    this.createdOn = createdOn;
    this.submittedOn = submittedOn;
    this.approvedOn = approvedOn;
    this.approvalComment = approvalComment;
    this.retiredOn = retiredOn;
    this.creativeFiles = creativeFiles;
    this.timestamp = DateTime.local();
    this.lineItemIDs = lineItemIDs;
    this.lineItems = [];
    this.lastPlayedTime = lastPlayedTime;
    this.iabCategories = iabCategories;
    this.mediaOwnerApprovals = mediaOwnerApprovals;
    this.matchingCreativeSizes = [];
    this.missingCreativeSizes = [];
    this.creativeProperties = creativeProperties;
    this.languageIDs = languageIDs;
    this.runState = runState;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.prefetchType = prefetchType;
    this.prefetchSource = prefetchSource;
    this.prefetchID = prefetchID;
    this.prefetchCacheType = prefetchCacheType;
    this.prefetchAllSizesApproval = prefetchAllSizesApproval;
    this.prefetchSizes = prefetchSizes;
    this.isFallback = isFallback;
    this.isMobile = isMobile;
    this.trackingUrls = trackingUrls;
    this.mobileLineItemIDs = mobileLineItemIDs;
    this.networkIDs = networkIDs;
    this.networkExclusionIDs = networkExclusionIDs;
    this.locationIDs = locationIDs;
    this.locationExclusionIDs = locationExclusionIDs;
    this.siteIDs = siteIDs;
    this.siteExclusionIDs = siteExclusionIDs;
    this.unitIDs = unitIDs;
    this.unitExclusionIDs = unitExclusionIDs;
    this.unitPackIDs = unitPackIDs;
    this.unitPackExclusionIDs = unitPackExclusionIDs;
    this.mediaOwnerIDs = mediaOwnerIDs;
    this.geofenceGroupIDs = geofenceGroupIDs;
    this.geofenceGroupExclusionIDs = geofenceGroupExclusionIDs;
    this.marketIDs = marketIDs;
    this.marketExclusionIDs = marketExclusionIDs;
    this.mediaTypeIDs = mediaTypeIDs;
    this.mediaTypeExclusionIDs = mediaTypeExclusionIDs;
    this.daypart = daypart;
    this.preciseDaypart = preciseDaypart;
    this.packageCreatives = packageCreatives;
    this.creativeTargetingCriteria = creativeTargetingCriteria;
    this.overrideDefaultDuration = overrideDefaultDuration;
    this.unitTags = unitTags;
    this.unitTagExclusions = unitTagExclusions;
    this.estimatedStart = estimatedStart;
    this.estimatedEnd = estimatedEnd;
    this.creativeFormats = creativeFormats;
    this.creativeFormatExclusions = creativeFormatExclusions;
    this.inventoryTargetingID = inventoryTargetingID;
    this.creativeOrientations = creativeOrientations;
    this.creativeOrientationExclusions = creativeOrientationExclusions;
    this.creativeType = creativeType;
    this.co2eEmissionsScoreThreshold = co2eEmissionsScoreThreshold;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Creative.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Creative.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Creative.entityPropertiesCached = null;
    });
  }

  get advertiserName() {
    if (this.advertiser != null) {
      return this.advertiser.uniqueAdvertiser != null ? this.advertiser.uniqueAdvertiserName : this.advertiser.name;
    }
    return DASH;
  }

  get advertiserDescription() {
    if (this.advertiser != null) {
      return this.advertiser.uniqueAdvertiser != null
        ? `${this.advertiserName} (${this.advertiserDomain})`
        : this.advertiser.description;
    }
    return DASH;
  }

  get advertiserDomain() {
    if (this.advertiser != null) {
      return this.advertiser.domain != null ? this.advertiser.domain : this.advertiser.uniqueAdvertiserDomain;
    }
    return DASH;
  }

  get advertiserLogo() {
    if (this.advertiser != null) {
      return this.advertiser.uniqueAdvertiser != null ? this.advertiser.uniqueAdvertiserLogoUrl : null;
    }
    return DASH;
  }

  get isMissingCreativeSizes() {
    return this.missingCreativeSizes.length > 0;
  }

  get hasMatchingCreativeSizes() {
    return this.matchingCreativeSizes.length > 0;
  }

  get endpoint() {
    return this.prefetchType == null ? `/creatives/${this.id}` : `/externalcreatives/${this.id}`;
  }

  get estimatedStartFormatted() {
    return getValueOrDash(this.estimatedStart?.toLocaleString(DateTime.DATE_SHORT));
  }

  get estimatedEndFormatted() {
    return getValueOrDash(this.estimatedEnd?.toLocaleString(DateTime.DATE_SHORT));
  }

  get pendingApprovalEndpoint() {
    return this.prefetchType == null
      ? `/pendingapprovals/creatives/${this.id}`
      : `/pendingapprovals/externalcreatives/${this.id}`;
  }

  get status() {
    let status: string;
    let { mediaOwnerApprovals } = this;
    const organization = mapOrgAppState().organization;
    const { isSupplyOrganization, id } = organization;

    if (isSupplyOrganization) {
      mediaOwnerApprovals = this.mediaOwnerApprovals.filter((x) => x.ownerID === id);
    }

    if (this.state === Status.SUBMITTED && this.isDisplayVideo) {
      return 'approved';
    }

    if (this.state === Status.RETIRED) {
      status = 'retired';
    } else if (this.isMobile) {
      status = 'ready';
    } else if (mediaOwnerApprovals.length === 0) {
      status = 'draft';
      // For DSP : when submitted, the creative is waiting for media owners approvals
      // that means at the moment the mediaOwnerApprovals.length is 0 but not in DRAFT mode.
      if (!isSupplyOrganization && this.state === Status.SUBMITTED) {
        status = 'pendingApproval';
      }
    } else if (mediaOwnerApprovals.every((x) => x.state === 'A')) {
      status = 'approved';
    } else if (mediaOwnerApprovals.every((x) => x.state === 'R')) {
      status = 'rejected';
    } else if (mediaOwnerApprovals.every((x) => x.state === 'D')) {
      status = 'draft';
    } else if (mediaOwnerApprovals.filter((x) => x.state === 'A').length > 0) {
      status = 'partiallyApproved';
    } else if (mediaOwnerApprovals.filter((x) => x.state === 'R').length > 0) {
      status = 'partiallyRejected';
    } else {
      status = 'pendingApproval';
    }

    return status;
  }

  get isPartiallyApproved() {
    return this.status === 'partiallyApproved';
  }

  get approvedMediaOwnerApprovals() {
    if (this.mediaOwnerApprovals == null) {
      return [];
    }
    return this.mediaOwnerApprovals.filter((x) => x.state === 'A');
  }

  get isApprovedWithComplexApproval() {
    const approvals = this.approvedMediaOwnerApprovals;
    return approvals.length === 1 && approvals[0].inventoryTargetingId != null;
  }

  get iabCategoriesFormatted() {
    return this.iabCategories.length > 0 ? this.iabCategories.map((x) => x.name).join(', ') : DASH;
  }

  get runStateDescription() {
    if (this.runState === RunState.STOPPED) {
      return capitalize(i18n.global.t('states.paused'));
    }
    return capitalize(i18n.global.t('states.active'));
  }

  get isPaused() {
    return this.runState === RunState.STOPPED;
  }

  get hasPollenTargeting() {
    const refData = rootState.chosenMembershipType === 'exch' ? mapExchAppState() : mapOrgAppState().ReferenceData;

    if (
      this.creativeTargetingCriteria == null ||
      this.creativeTargetingCriteria.length === 0 ||
      refData.targetingCriteria == null ||
      refData.targetingCriteria.length === 0
    ) {
      return false;
    }
    const pollenTargetCriteria = refData.targetingCriteria.filter(
      (x: TargetingCriterion) => x.type === TargetingCriteriaType.Pollen,
    );
    return this.creativeTargetingCriteria.some((x) =>
      pollenTargetCriteria.some((y: TargetingCriterion) => y.id === x.targetingCriterionID),
    );
  }

  get hasWeatherTargeting() {
    const refData = rootState.chosenMembershipType === 'exch' ? mapExchAppState() : mapOrgAppState().ReferenceData;

    if (
      this.creativeTargetingCriteria == null ||
      this.creativeTargetingCriteria.length === 0 ||
      refData.targetingCriteria == null ||
      refData.targetingCriteria.length === 0
    ) {
      return false;
    }

    const weatherAndTempTargetCriteria = refData.targetingCriteria.filter(
      (x: TargetingCriterion) =>
        x.type === TargetingCriteriaType.Weather || x.type === TargetingCriteriaType.Temperature,
    );
    return (
      this.creativeTargetingCriteria.filter(
        (x) => weatherAndTempTargetCriteria.find((y: TargetingCriterion) => y.id === x.targetingCriterionID) != null,
      ).length > 0
    );
  }

  get hasCustomEvents() {
    const refData = rootState.chosenMembershipType === 'exch' ? mapExchAppState() : mapOrgAppState().ReferenceData;

    if (
      this.creativeTargetingCriteria == null ||
      this.creativeTargetingCriteria.length === 0 ||
      refData.targetingCriteria == null ||
      refData.targetingCriteria.length === 0
    ) {
      return false;
    }

    const externalPushedTargetCriteria = refData.targetingCriteria.filter(
      (x: TargetingCriterion) => x.isUpdatePushedExternally,
    );
    return this.creativeTargetingCriteria.some((x) =>
      externalPushedTargetCriteria.some((ec: TargetingCriterion) => ec.id === x.targetingCriterionID),
    );
  }

  get targetingTypes() {
    const types = [];
    if (!this.isDaypartEmptyOrFull) {
      types.push({
        id: 'daypart',
        iconColored: IconColoredDaypart,
        icon: markRaw(IconDaypart),
        name: capitalize(i18n.global.t('names.daypart')),
      });
    }
    if (this.hasInventoryTargeting) {
      types.push({
        id: 'inventory',
        iconColored: IconColoredGeography,
        icon: markRaw(IconGeography),
        name: capitalize(i18n.global.t('names.inventory')),
      });
    }
    if (this.hasWeatherTargeting) {
      types.push({
        id: 'weather',
        iconColored: IconColoredWeather,
        icon: markRaw(IconWeather),
        name: capitalize(i18n.global.t('names.weatherCondition', { n: 2 })),
      });
    }
    if (this.hasCustomEvents) {
      types.push({
        id: 'custom-events',
        iconColored: IconColoredEvent,
        icon: markRaw(IconEvent),
        name: capitalize(i18n.global.t('names.customEvent', { n: 2 })),
      });
    }
    if (this.hasPollenTargeting) {
      types.push({
        id: 'custom-events',
        iconColored: IconColoredPollen,
        icon: markRaw(IconEvent),
        name: capitalize(i18n.global.t('names.pollen', { n: 2 })),
      });
    }
    return types;
  }

  get isPausedDescription() {
    if (this.runState === 'S') {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get isActiveDescription() {
    if (this.runState === 'S') {
      return capitalize(i18n.global.t('actions.no'));
    }
    return capitalize(i18n.global.t('actions.yes'));
  }

  get createdOnFormatted() {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get submittedOnFormatted() {
    return this.submittedOn != null ? this.submittedOn.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get lastPlayedTimeFormatted() {
    return this.lastPlayedTime != null ? this.lastPlayedTime.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get prefetchSizePendingApprovalCount() {
    if (this.prefetchAllSizesApproval !== null) {
      return 0;
    }

    let count = 0;
    for (let idx = 0; idx < this.prefetchSizes.length; idx += 1) {
      const prefetchSize = this.prefetchSizes[idx];
      if (prefetchSize.approval === PrefetchSizeApprovalStatus.PENDING) {
        count += 1;
      }
    }
    return count;
  }

  get prefetchSizeCount() {
    return this.prefetchSizes.length;
  }

  get creativeFilePendingApprovalCount() {
    let count = 0;
    for (let idx = 0; idx < this.creativeFiles.length; idx += 1) {
      const creativeFile = this.creativeFiles[idx];
      if (creativeFile.prefetchApproval === CreativeFileApprovalStatus.PENDING) {
        count += 1;
      }
    }
    return count;
  }

  // return the first creative file that is not archived, useful for creative-previewer
  get creativeFileForPreview(): CreativeFile | null {
    return this.creativeFiles.find((x) => !x.isArchived) || null;
  }

  get creativeFileCount() {
    return this.creativeFiles.length;
  }

  get targetParams() {
    return {
      location_ids: this.locationIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      unit_ids: this.unitIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      site_ids: this.siteIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      market_ids: this.marketIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      daypart: this.daypart,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      demographic_ids: [],
      from_date: toServerDate(DateTime.local().startOf('week')),
      to_date: toServerDate(DateTime.local().endOf('week').startOf('day')),
      ad_duration: 0,
      screen_frequency_cap: 0,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_orientations: this.creativeOrientations,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
    };
  }

  get hasInventoryTargeting() {
    return !(
      (this.marketIDs == null || this.marketIDs.length === 0) &&
      (this.marketExclusionIDs == null || this.marketExclusionIDs.length === 0) &&
      (this.locationIDs == null || this.locationIDs.length === 0) &&
      (this.locationExclusionIDs == null || this.locationExclusionIDs.length === 0) &&
      (this.mediaTypeIDs == null || this.mediaTypeIDs.length === 0) &&
      (this.mediaTypeExclusionIDs == null || this.mediaTypeExclusionIDs.length === 0) &&
      (this.networkIDs == null || this.networkIDs.length === 0) &&
      (this.networkExclusionIDs == null || this.networkExclusionIDs.length === 0) &&
      (this.unitIDs == null || this.unitIDs.length === 0) &&
      (this.unitExclusionIDs == null || this.unitExclusionIDs.length === 0) &&
      (this.siteIDs == null || this.siteIDs.length === 0) &&
      (this.siteExclusionIDs == null || this.siteExclusionIDs.length === 0) &&
      (this.geofenceGroupIDs == null || this.geofenceGroupIDs.length === 0) &&
      (this.geofenceGroupExclusionIDs == null || this.geofenceGroupExclusionIDs.length === 0) &&
      (this.unitTags == null || this.unitTags.length === 0) &&
      (this.unitTagExclusions == null || this.unitTagExclusions.length === 0) &&
      (this.creativeFormats == null || this.creativeFormats.length === 0) &&
      (this.creativeFormatExclusions == null || this.creativeFormatExclusions.length === 0) &&
      (this.creativeOrientations == null || this.creativeOrientations.length === 0) &&
      (this.creativeOrientationExclusions == null || this.creativeOrientationExclusions.length === 0)
    );
  }

  get isDaypartFull() {
    if (this.preciseDaypart) {
      return this.preciseDaypart.indexOf('0') === -1;
    }

    return this.daypart.indexOf('0') === -1;
  }

  get isDaypartEmpty() {
    if (this.preciseDaypart) {
      return this.preciseDaypart.indexOf('1') === -1;
    }

    return this.daypart.indexOf('1') === -1;
  }

  get isDaypartEmptyOrFull() {
    return this.isDaypartEmpty || this.isDaypartFull;
  }

  get idAndName() {
    return `${this.id} - ${this.name}`;
  }

  get isDraft() {
    return this.state === Status.DRAFT;
  }

  get daypartRanges() {
    return this.daypart == null || this.daypart === FullDaypart ? DASH : daypartToRanges(this.daypart);
  }

  get preciseDaypartRanges() {
    return this.preciseDaypart == null || this.preciseDaypart === FullPreciseDaypart
      ? DASH
      : preciseDaypartToRanges(this.preciseDaypart);
  }

  get isVideo() {
    return this.creativeType === CreativeType.Video;
  }

  get isBanner() {
    return this.creativeType === CreativeType.Banner;
  }

  get isDisplayVideo() {
    return this.isBanner || this.isVideo;
  }

  get creativeTypeFormatted() {
    return this.creativeType == null
      ? DASH
      : CreativeTypeOptions.displayVideoOptions().find((x) => x.value === this.creativeType).name || DASH;
  }

  getFromDateForLineItemId(lineItemID) {
    const lineItemCreative = this.lineItems.find((x) => x.lineItemID === lineItemID);
    if (!lineItemCreative) {
      return '';
    }
    return lineItemCreative.fromDate;
  }

  getFromDateAsPercentageOfLineItemDateRange(lineItemID: number, startDate: DateTime, endDate: DateTime) {
    const lineItemCreative = this.lineItems.find((x) => x.lineItemID === lineItemID);
    if (!lineItemCreative) {
      return '';
    }
    return lineItemCreative.fromDate == null
      ? 0
      : (lineItemCreative.fromDate.diff(startDate, 'seconds').toObject().seconds /
          endDate.plus({ days: 1 }).diff(startDate, 'seconds').toObject().seconds) *
          100;
  }

  setFromDateForLineItemId(lineItemID: number, val: DateTime) {
    const idx = this.lineItems.findIndex((x) => x.lineItemID === lineItemID);
    if (idx === -1) {
      throw Error(`Unknown lineitem ${lineItemID} for creative`);
    }
    this.lineItems[idx].fromDate = val;
  }

  getToDateForLineItemId(lineItemID: number) {
    const lineItemCreative = this.lineItems.find((x) => x.lineItemID === lineItemID);
    if (!lineItemCreative) {
      return '';
    }
    return lineItemCreative.toDate;
  }

  getToDateAsPercentageOfLineItemDateRange(lineItemID: number, startDate: DateTime, endDate: DateTime) {
    const lineItemCreative = this.lineItems.find((x) => x.lineItemID === lineItemID);
    if (!lineItemCreative) {
      return '';
    }
    return lineItemCreative.toDate == null
      ? 100
      : (lineItemCreative.toDate.plus({ days: 1 }).diff(startDate, 'seconds').toObject().seconds /
          endDate.plus({ days: 1 }).diff(startDate, 'seconds').toObject().seconds) *
          100;
  }

  setToDateForLineItemId(lineItemID: number, val: DateTime) {
    const idx = this.lineItems.findIndex((x) => x.lineItemID === lineItemID);
    if (idx === -1) {
      throw Error(`Unknown lineitem ${lineItemID} for creative`);
    }
    this.lineItems[idx].toDate = val;
  }

  getWeightForLineItemId(lineItemID: number): number {
    const lineItemCreative = this.lineItems.find((x) => x.lineItemID === lineItemID);
    if (!lineItemCreative) {
      return null;
    }
    return lineItemCreative.weight;
  }

  setWeightForLineItemId(lineItemID: number, val: number) {
    const idx = this.lineItems.findIndex((x) => x.lineItemID === lineItemID);
    if (idx === -1) {
      throw Error(`Unknown lineitem ${lineItemID} for creative`);
    }
    this.lineItems[idx].weight = val;
  }

  percentageWeight(sumWeight: number, lineItemID: number) {
    if (this.isPaused) return (0).toFixed(1);
    const weight = this.getWeightForLineItemId(lineItemID);
    if (weight == null) return 0;
    return ((weight / sumWeight) * 100).toFixed(1);
  }

  get isPlaying() {
    return this.runState === RunState.PLAYING;
  }

  // #region used for TargetingEntityHelper
  get hasCadencePurchaseType() {
    return false;
  }

  get purchaseType() {
    return null;
  }

  get hasStopped() {
    return this.runState === RunState.STOPPED;
  }

  get isTakeoverEnabled() {
    return false;
  }

  get targetingCriteria(): CreativeTargetingCriterion[] {
    return this.creativeTargetingCriteria || [];
  }
  // #endregion

  getExtraValidation() {
    const extras: { [name: string]: { message?: string; preEvaluated?: boolean } } = {};

    //
    // Validate creative properties
    //
    if (this.creativeProperties != null) {
      const hasCreativePropertiesWithEmptyKeys = this.creativeProperties.filter(
        (x) => x.key.trim() === '' || x.key === null,
      );
      const hasCreativePropertiesWithEmptyValues = this.creativeProperties.filter(
        (x) => x.value.trim() === '' || x.value === null,
      );
      const hasCreativePropertiesWithDuplicatedKeys = Object.values(groupBy(this.creativeProperties, 'key')).filter(
        (x) => x.length > 1,
      ).length;
      if (hasCreativePropertiesWithEmptyKeys.length > 0) {
        extras.creativeProperties = {
          message: i18n.global.t('messages.creativePropertyKeysCannotBeEmpty'),
          preEvaluated: true,
        };
      }
      if (hasCreativePropertiesWithEmptyValues.length > 0) {
        extras.creativeProperties = {
          message: i18n.global.t('messages.creativePropertyValuesCannotBeEmpty'),
          preEvaluated: true,
        };
      }
      if (hasCreativePropertiesWithDuplicatedKeys) {
        extras.creativePropertiesKey = {
          message: i18n.global.t('messages.duplicateKey'),
          preEvaluated: true,
        };
      }
    }
    //
    // Validate languages
    //
    if (this.languageIDs.length === 0) {
      extras.languages = {
        message: i18n.global.t('messages.creativeLanguagesCannotBeEmpty'),
        preEvaluated: true,
      };
    }

    //
    // Validate tracking urls
    //
    if (this.trackingUrls != null && this.trackingUrls.filter((x) => !isValidTokenizedURL(x.trackingUrl)).length > 0) {
      extras.creativeTrackingUrls = {
        message: i18n.global.t('messages.oneOrMoreTrackingURLsAreInvalid'),
        preEvaluated: true,
      };
    }

    //
    // Validate mobile and fallback
    //
    if (this.isMobile && this.isFallback) {
      extras.isMobile = {
        message: i18n.global.t('messages.creativeCanBeMobileOrFallbackNotBoth'),
        preEvaluated: true,
      };
      extras.isFallback = {
        message: i18n.global.t('messages.creativeCanBeMobileOrFallbackNotBoth'),
        preEvaluated: true,
      };
    }

    return extras;
  }

  get didNotPlayInTheLast24Hours() {
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast24Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 24;
  }

  get didNotPlayInTheLast12Hours() {
    if (this.lastPlayedTime == null) {
      return false; // Unknown...
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return false;
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours > 12;
  }

  get didPlayInTheLast12Hours() {
    if (this.lastPlayedTime == null) {
      return false; // Did not start playing.
    }
    if (!this.hasDaypartEnabledInTheLast12Hours) {
      return true; // Not really playing but still should show a green thumb in the UI.
    }
    return DateTime.local().diff(this.lastPlayedTime, 'hours').toObject().hours <= 12;
  }

  get hasDaypartEnabledInTheLast24Hours() {
    return this.hasDaypartEnabledInTheLastXHours(24);
  }

  get hasDaypartEnabledInTheLast12Hours() {
    return this.hasDaypartEnabledInTheLastXHours(12);
  }

  hasDaypartEnabledInTheLastXHours(hours = 24) {
    const startOfWeek = DateTime.local().startOf('week');
    const hoursSinceStartOfWeek = DateTime.local().startOf('hour').diff(startOfWeek, 'hours').toObject().hours;
    const ranges = [
      {
        start: Math.max(0, hoursSinceStartOfWeek - hours),
        end: hoursSinceStartOfWeek,
      },
    ];
    if (hoursSinceStartOfWeek < hours) {
      ranges.push({
        start: this.daypart.length + hoursSinceStartOfWeek - hours,
        end: this.daypart.length,
      });
    }
    return ranges.reduce((mem, x) => mem || this.daypart.slice(x.start, x.end - x.start).includes('1'), false);
  }

  get targetCriteriaWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Weather,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Temperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaUV() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.UV,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allUVs().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationWeather,
    );

    if (weatherTargetingCriterion == null) return null;

    const weatherTargetingCriteria = this.targetingCriteria.filter(
      (tc) => tc.targetingCriterionID === weatherTargetingCriterion.id,
    );

    return weatherTargetingCriteria
      .map((tc) => {
        const weather = tc.values
          .filter((v) => v.code === 'WEATHER')
          .map(
            (v) =>
              v.value.split(',').map((v2) => {
                return WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name;
              }) || [],
          );
        const latLng = tc.values
          .filter((v) => v.code === 'LOCATION')
          .map((v) => v.value)
          .join(', ');

        return latLng
          ? `${weather.join(', ')} ${latLng} (${i18n.global.t('names.latitude')}, ${i18n.global.t('names.longitude')})`
          : weather.join(', ');
      })
      .flat();
  }

  get targetCriteriaDestinationTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationTemperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationRain() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationRain,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationSnow() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationSnow,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaPollen() {
    const pollenTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Pollen,
    );
    return pollenTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === pollenTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allPollens().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaCustomEvents() {
    const customEvents = mapOrgAppState().ReferenceData.targetingCriteria.filter((x) => x.isUpdatePushedExternally);
    const customEventSubtypes = customEvents.flatMap((ce) => ce.subTypes);
    const customEventIDs = customEvents.map((ce) => ce.id);
    return customEvents.length
      ? this.targetingCriteria
          .filter((tc) => customEventIDs.includes(tc.targetingCriterionID))
          .map((tc) =>
            tc.values.map(
              (x) =>
                `${customEventSubtypes.find((st) => st.code === x.code).categoryName}: ${customEventSubtypes.find((st) => st.code === x.code).name}`,
            ),
          )
          .flat()
      : null;
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'advertiser',
      'creative_files',
      'flight_from',
      'flight_to',
      'inventory_targeting',
      'lineitem_creatives',
      'mediaowner_approvals',
      'last_played_time_utc',
      'organization',
      'approval_at',
      'approval_comment',
      'archived',
      'inventorytargeting_id',
      'trackingurls',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties, ['precise_daypart']);
    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  static fromApi(apiObject: ICreativeAPI) {
    const iabCategoryOptions = IABCategories.options();
    const trackingUrls = (apiObject.tracking || []).map(
      (x) => new CreativeTrackingUrl(-1, apiObject.creative_id, x.url, x.multiple_calls),
    );

    const creative = Creative.fromObject({
      id: apiObject.creative_id,
      name: apiObject.name,
      uuid: apiObject.uuid,
      advertiserID: apiObject.advertiser_id,
      advertiser: apiObject.advertiser != null ? Advertiser.fromApi(apiObject.advertiser) : null,
      organizationID: apiObject.organization_id,
      organizationName: apiObject.organization !== undefined ? Organization.fromApi(apiObject.organization).name : null,
      state: apiObject.state,
      createdOn: apiObject.created_at != null ? fromServerDateTime(apiObject.created_at) : null,
      submittedOn: apiObject.submitted_at != null ? DateTime.fromISO(apiObject.submitted_at, { zone: 'utc' }) : null,
      approvedOn: apiObject.approval_at != null ? fromServerDateTime(apiObject.approval_at) : null,
      approvalComment: apiObject.approval_comment,
      retiredOn: apiObject.retired_at != null ? fromServerDateTime(apiObject.retired_at) : null,
      lineItems:
        apiObject.lineitem_creatives != null
          ? apiObject.lineitem_creatives.map((x) => LineItemCreative.fromApi(x))
          : [],
      creativeFiles:
        apiObject.creative_files != null ? apiObject.creative_files.map((x) => CreativeFile.fromApi(x)) : [],
      iabCategories:
        apiObject.iab_categories != null
          ? apiObject.iab_categories
              .filter((x) => iabCategoryOptions.some((o) => o.id === x)) // PRO-2319 - This handled unknown categories (e.g.: IAB22-7)
              .map((x) => iabCategoryOptions.find((o) => o.id === x))
          : [],
      mediaOwnerApprovals:
        apiObject.mediaowner_approvals != null
          ? apiObject.mediaowner_approvals.map((x) => CreativeMediaApproval.fromApi(x))
          : [],
      creativeProperties:
        apiObject.creative_properties != null
          ? apiObject.creative_properties.map((x) => CreativeProperty.fromApi(x))
          : [],
      languageIDs: apiObject.language_ids,
      lastPlayedTime:
        apiObject.last_played_time_utc != null
          ? DateTime.fromSeconds(apiObject.last_played_time_utc)
          : apiObject.last_played_time_utc,
      runState: apiObject.run_state,
      prefetchType: apiObject.prefetch_type,
      prefetchSource: apiObject.prefetch_source,
      prefetchID: apiObject.prefetch_id,
      prefetchCacheType: apiObject.prefetch_cache_type,
      prefetchAllSizesApproval: apiObject.prefetch_all_sizes_approval,
      prefetchSizes:
        apiObject.prefetch_sizes != null ? apiObject.prefetch_sizes.map((x) => CreativePrefetchSize.fromApi(x)) : [],
      isFallback: apiObject.is_fallback == null ? false : apiObject.is_fallback,
      isMobile: apiObject.is_mobile || false,
      trackingUrls,
      locationIDs: apiObject.inventory_targeting?.location_ids || [],
      locationExclusionIDs: apiObject.inventory_targeting?.location_exclusion_ids || [],
      geofenceGroupIDs: apiObject.inventory_targeting?.geofencegroup_ids || [],
      geofenceGroupExclusionIDs: apiObject.inventory_targeting?.geofencegroup_exclusion_ids || [],
      marketIDs: apiObject.inventory_targeting?.market_ids || [],
      marketExclusionIDs: apiObject.inventory_targeting?.market_exclusion_ids || [],
      mediaTypeIDs: apiObject.inventory_targeting?.mediatype_ids,
      mediaTypeExclusionIDs: apiObject.inventory_targeting?.mediatype_exclusion_ids || [],
      networkIDs: apiObject.inventory_targeting?.network_ids || [],
      networkExclusionIDs: apiObject.inventory_targeting?.network_exclusion_ids || [],
      siteIDs: apiObject.inventory_targeting?.site_ids || [],
      siteExclusionIDs: apiObject.inventory_targeting?.site_exclusion_ids || [],
      unitIDs: apiObject.inventory_targeting?.unit_ids || [],
      unitExclusionIDs: apiObject.inventory_targeting?.unit_exclusion_ids || [],
      unitPackIDs: apiObject.inventory_targeting?.unitpack_ids || [],
      unitPackExclusionIDs: apiObject.inventory_targeting?.unitpack_exclusion_ids || [],
      mediaOwnerIDs: apiObject.inventory_targeting?.owner_ids,
      daypart: apiObject.daypart,
      preciseDaypart: apiObject.precise_daypart,
      packageCreatives: apiObject.package_creatives,
      creativeTargetingCriteria:
        apiObject.creative_targetingcriteria != null
          ? apiObject.creative_targetingcriteria.map((x) => CreativeTargetingCriterion.fromApi(x))
          : [],
      overrideDefaultDuration:
        apiObject.override_default_duration == null ? false : apiObject.override_default_duration,
      unitTags: apiObject.unit_tags,
      unitTagExclusions: apiObject.unit_tag_exclusions,
      estimatedStart: fromServerDate(apiObject.flight_from),
      estimatedEnd: fromServerDate(apiObject.flight_to),
      creativeFormats: apiObject.creative_formats,
      creativeFormatExclusions: apiObject.creative_format_exclusions,
      inventoryTargetingID: apiObject.inventory_targeting?.inventorytargeting_id,
      creativeOrientations: apiObject.inventory_targeting?.creative_orientations || [],
      creativeOrientationExclusions: apiObject.inventory_targeting?.creative_orientation_exclusions || [],
      creativeType: apiObject.creative_type,
      co2eEmissionsScoreThreshold: apiObject.inventory_targeting?.co2e_emissions_score_threshold,
    });
    creative.apiObject = apiObject;
    return creative;
  }

  get inventoryTargeting(): InventoryTargeting {
    return new InventoryTargeting(
      this.inventoryTargetingID,
      this.creativeFormatExclusions,
      this.creativeFormats,
      this.creativeOrientationExclusions,
      this.creativeOrientations,
      this.geofenceGroupExclusionIDs,
      this.geofenceGroupIDs,
      [],
      [],
      [],
      [],
      this.locationExclusionIDs,
      this.locationIDs,
      this.marketExclusionIDs,
      this.marketIDs,
      this.mediaTypeExclusionIDs,
      this.mediaTypeIDs,
      this.networkExclusionIDs,
      this.networkIDs,
      this.mediaOwnerIDs,
      this.siteExclusionIDs,
      this.siteIDs,
      this.unitExclusionIDs,
      this.unitIDs,
      this.unitTagExclusions,
      this.unitTags,
      this.unitPackExclusionIDs,
      this.unitPackIDs,
    );
  }

  get originalObject(): Creative {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Creative.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  async getChanges(): Promise<IEntityChange<Creative>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Creative>(this.originalObject, Creative.entityProperties);
  }

  static fromObject(obj) {
    return super.fromObject(obj, new Creative());
  }

  toBulkUpdateApi() {
    const apiObject = this.toApi();
    return {
      state: apiObject.state,
      run_state: apiObject.run_state,
    };
  }

  toApi(
    options: { includeCreativeFiles: boolean; includeLineItems: boolean } = {
      includeCreativeFiles: false,
      includeLineItems: false,
    },
  ): ICreativeAPI {
    return {
      advertiser: undefined,
      flight_from: undefined,
      flight_to: undefined,
      last_played_time_utc: undefined,
      mediaowner_approvals: undefined,
      organization: undefined,

      uuid: this.uuid,
      creative_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      advertiser_id: this.advertiserID,
      state: this.state,
      created_at: toServerDateTime(this.createdOn),
      submitted_at: toServerDateTime(this.submittedOn),
      approval_at: toServerDateTime(this.approvedOn),
      approval_comment: this.approvalComment,
      retired_at: toServerDateTime(this.retiredOn),
      iab_categories: this.iabCategories != null ? this.iabCategories.map((x) => x.id) : [],
      creative_properties: this.creativeProperties.map((x) => x.toApi()),
      creative_files: options.includeCreativeFiles ? this.creativeFiles.map((x) => x.toApi()) : null,
      language_ids: this.languageIDs,
      lineitem_creatives: options.includeLineItems ? this.lineItems.map((x) => x.toApi()) : null,
      organization_id: this.organizationID,
      run_state: this.runState,
      prefetch_type: this.prefetchType,
      prefetch_source: this.prefetchType != null ? this.prefetchSource : null,
      prefetch_id: this.prefetchType != null ? this.prefetchID : null,
      prefetch_cache_type: this.prefetchType != null ? this.prefetchCacheType : null,
      prefetch_all_sizes_approval: this.prefetchAllSizesApproval,
      prefetch_sizes: this.prefetchSizes.map((x) => x.toApi()),
      is_fallback: this.isFallback,
      is_mobile: this.isMobile,
      tracking:
        this.trackingUrls != null
          ? this.trackingUrls.map((x) => ({
              url: x.trackingUrl,
              multiple_calls: x.multipleCalls,
            }))
          : null,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      location_ids: this.locationIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      site_ids: this.siteIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      unit_ids: this.unitIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      market_ids: this.marketIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      daypart: this.daypart,
      precise_daypart: this.preciseDaypart,
      package_creatives: this.packageCreatives,
      creative_targetingcriteria:
        this.creativeTargetingCriteria != null ? this.creativeTargetingCriteria.map((x) => x.toApi()) : undefined,
      override_default_duration: this.overrideDefaultDuration,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_type: this.creativeType,
      inventory_targeting: {
        inventorytargeting_id: this.inventoryTargetingID,
        market_ids: this.marketIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        owner_ids: this.mediaOwnerIDs,
        location_ids: this.locationIDs,
        location_exclusion_ids: this.locationExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        network_ids: this.networkIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        site_ids: this.siteIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        unit_ids: this.unitIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        unit_tags: this.unitTags,
        unit_tag_exclusions: this.unitTagExclusions,
        unitpack_ids: this.unitPackIDs,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        creative_formats: this.creativeFormats,
        creative_format_exclusions: this.creativeFormatExclusions,
        creative_orientations: this.creativeOrientations,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        iab_categories: [], // HV-9581 - IAB categories are set at the root of the entity, never in the inventory targeting
        iab_category_exclusions: [],
        language_ids: [], // for a creative, language is set at the root of the entity, never in the inventory targeting
        language_exclusion_ids: [],
        co2e_emissions_score_threshold: this.co2eEmissionsScoreThreshold,
      },
    } as ICreativeAPI;
  }

  toApiStateChange(addEmptyInventoryTargeting: boolean = false) {
    // const changes = {};
    // if (this.name !== this.originalEntity.name) { changes.name = this.name; }
    // if (this.startDate.equals(this.originalEntity.startDate)) { changes.name = this.name; }
    // return changes;
    return addEmptyInventoryTargeting
      ? {
          state: this.state,
          approval_comment: this.approvalComment,
          inventory_targeting: new InventoryTargeting(),
        }
      : {
          state: this.state,
          approval_comment: this.approvalComment,
        };
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      advertiserID: 'required',
    };
  }

  copy() {
    const copy = this.clone();
    copy.advertiser = this.advertiser;
    return copy;
  }

  clone() {
    return Creative.fromApi(
      this.toApi({
        includeLineItems: true,
        includeCreativeFiles: true,
      }),
    );
  }

  get languagesDescription() {
    const allLanguages = peekQueryInScope(useLanguages);
    const languages = this.languageIDs?.map((id) => allLanguages.find((l) => l.id === id)) ?? [];
    if (languages.length === 0 || languages.filter((x) => x == null).length > 0) {
      return DASH;
    }
    return languages.map((x) => x.name).join(', ');
  }

  get isAllowedToSaveWithoutApprovals() {
    return (
      mapOrgAppState().organization?.isSupplyOrganization &&
      this.mediaOwnerApprovals.length === 1 &&
      this.mediaOwnerApprovals[0].networkID === -1 &&
      this.mediaOwnerApprovals[0].state === MediaOwnerApprovalStatus.APPROVED
    );
  }

  static get bulkEditProperties() {
    return [
      this.entityProperties.advertiser,
      this.entityProperties.languages,
      this.entityProperties.isFallback,
      this.entityProperties.isMobile,
      this.entityProperties.runState,
    ];
  }

  static get bulkV4Properties() {
    return [
      this.entityProperties.name,
      this.entityProperties.advertiser,
      this.entityProperties.languages,
      // this.entityProperties.isMobile,
      this.entityProperties.runState,
      this.entityProperties.iabCategories,
      this.entityProperties.isFallback,
    ];
  }

  static get entityName() {
    return type;
  }

  static get entityProperties() {
    if (Creative.entityPropertiesCached != null) {
      return Creative.entityPropertiesCached;
    }
    Creative.entityPropertiesCached = getEntityProperties();
    return Creative.entityPropertiesCached;
  }

  static async getEntityIDsFromModels(models) {
    if (models.length === 0) return [];

    try {
      const names = models.map((x) => x.name);
      const getCreativesAction = ({ queryParams, showLoader }: { queryParams?: QueryParams; showLoader: boolean }) =>
        rootStore.dispatch('orgApp/getCreatives', { queryParams, showLoader });
      const qp = QueryParams.all(FilterCollection.for('name', names, FilterComparisonOperator.IN));
      qp.include = ['creative_id', 'name'];
      const response = await qp.run(getCreativesAction);
      const entities = response.items;
      return models.map((model) => ({ model, id: entities.find((x) => x.name === model.name)?.id }));
    } catch (e) {
      throw new Exception('Failed to get creative IDs.');
    }
  }

  static async assignIDs(entities) {
    if (entities.length === 0) return [];

    const existingIDMapping = await Creative.getEntityIDsFromModels(entities);
    entities.forEach((x) => {
      x.id = existingIDMapping.find((y) => y.model === x)?.id || -1;
    });
    return entities;
  }

  static async patchEntities(entities) {
    try {
      const payload = this.getPatchPayload(entities);
      await rootStore.dispatch('orgApp/patchCreatives', payload);
    } catch (e) {
      throw new Exception('Failed to patch creatives.');
    }
  }

  static async getEntities(ids) {
    if (ids.length === 0) return [];

    try {
      const queryParams = QueryParams.all(FilterCollection.for('creative_id', ids, FilterComparisonOperator.IN));
      queryParams.showLoader = false;
      const response = await rootStore.dispatch('orgApp/getCreatives', {
        queryParams,
        cancelTokenSource: queryParams.cancelTokenSource,
        showLoader: false,
      });
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get creatives.');
    }
  }
}
